import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { NestedFormulaProvider } from "@/components/formula/nested-formula-context";
import { AlertTriangleIcon } from "lucide-react";

// Shared, domain-agnostic UI for the template-correction flow. Both the harvest
// and test flow components compose these; the domain components own the tRPC
// lifecycle hooks and the correction editor.

export type CorrectionStep = "scope" | "edit" | "review";

export interface InstanceOption {
  id: number;
  label: string;
  sublabel: string | null;
  status: string | null;
  sessionId: number | null;
  sessionLabel: string | null;
}

export interface CorrectionPreviewData {
  hasStructural: boolean;
  blockers: { type: string; message: string }[];
  deltas: {
    instanceId: number;
    valuesLost: number;
    dataLoss: boolean;
    ops: {
      opType: string;
      attributeName: string;
      columnName?: string;
      valuesLost: number;
      cellsRewritten: number;
      structural: boolean;
      dataLoss: boolean;
    }[];
  }[];
  summary: {
    attributesAdded: number;
    attributesDeleted: number;
    columnsAdded: number;
    columnsDeleted: number;
    columnsRenamed: number;
    valuesLost: number;
    cellsRewritten: number;
    instancesWithDataLoss: number;
  };
}

function formatStatus(status: string | null): string {
  if (!status) return "";
  return status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, " ");
}

// Harvest name (sublabel) reads first, operation/instance name (label) second.
function InstanceCaption({ o }: { o: InstanceOption }) {
  const primary = o.sublabel ?? o.label;
  const secondary = o.sublabel ? o.label : null;
  return (
    <span className="flex items-baseline gap-2 min-w-0">
      <span className="text-sm truncate">{primary}</span>
      {secondary && (
        <span className="text-xs text-olive-9 truncate">{secondary}</span>
      )}
    </span>
  );
}

const STEPS: { key: CorrectionStep; label: string }[] = [
  { key: "scope", label: "1 · Instances" },
  { key: "edit", label: "2 · Edit template" },
  { key: "review", label: "3 · Review & apply" },
];

export function FullScreenDialog({
  open,
  onOpenChange,
  children,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  children: React.ReactNode;
}) {
  // Matches the formula editor: a large-but-inset modal (viewport minus 4rem)
  // over the standard dialog overlay. DialogContent owns the overlay + close.
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col w-[calc(100vw-4rem)] h-[calc(100vh-4rem)] max-w-none max-h-none p-0 gap-0">
        <DialogTitle className="sr-only">Edit template</DialogTitle>
        <NestedFormulaProvider>{children}</NestedFormulaProvider>
      </DialogContent>
    </Dialog>
  );
}

// Shown when the dialog is closed with an editable draft (W) still open.
export function CloseConfirmDialog({
  open,
  onKeep,
  onDiscard,
  onCancel,
}: {
  open: boolean;
  onKeep: () => void;
  onDiscard: () => void;
  onCancel: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Leave this correction?</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved edits in this correction draft. Discard them, or keep the
            draft to resume later from the revision history.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="ghost" size="sm" onClick={onKeep}>
            Keep draft
          </Button>
          <Button
            size="sm"
            className="bg-red-600 text-white hover:bg-red-700"
            onClick={onDiscard}
          >
            Discard
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Edit step: replace the working copy with the template's current version.
// The hint shows only when the source differs from the current template.
export function CurrentTemplateBar({
  outdatedMessage,
  pending,
  onUseCurrent,
}: {
  outdatedMessage: string | null;
  pending: boolean;
  onUseCurrent: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <div
      className={cn(
        "flex items-center gap-3 px-4 py-2 border-b border-border shrink-0",
        outdatedMessage && "bg-amber-3",
      )}
    >
      {outdatedMessage && (
        <AlertTriangleIcon className="size-4 text-amber-11 shrink-0" />
      )}
      <span className={cn("text-sm flex-1", outdatedMessage ? "text-amber-11" : "text-olive-11")}>
        {outdatedMessage ?? "Start from the template as it is now, or edit this version."}
      </span>
      <Button
        size="sm"
        variant={outdatedMessage ? "default" : "ghost"}
        disabled={pending}
        onClick={() => setConfirming(true)}
      >
        {pending ? "Loading…" : "Use the current template"}
      </Button>
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Use the current template?</AlertDialogTitle>
            <AlertDialogDescription>
              The fields in this correction are replaced with the template as it
              is now. Edits you already made in this correction are lost. The
              review step shows what changes for the selected instances.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setConfirming(false);
                onUseCurrent();
              }}
            >
              Use the current template
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function CorrectionHeader({
  title,
  subtitle,
  step,
}: {
  title: string;
  subtitle?: string;
  step: CorrectionStep;
}) {
  return (
    <div className="flex items-center justify-between border-b border-border px-4 h-12 shrink-0 pr-12">
      <div className="flex items-baseline gap-3">
        <span className="text-base font-medium">{title}</span>
        {subtitle && <span className="text-sm text-olive-11">{subtitle}</span>}
      </div>
      <div className="flex items-center gap-1">
        {STEPS.map((s) => (
          <span
            key={s.key}
            className={cn(
              "px-2 py-1 text-xs",
              s.key === step
                ? "text-[var(--farmless-green)] font-medium"
                : "text-olive-9",
            )}
          >
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// Step 1: choose which instances the correction applies to. Session-bound
// instances are selected as a whole session (all-or-none).
export function ScopeStep({
  options,
  selected,
  onToggle,
  onToggleSession,
  preselectedHidden = false,
}: {
  options: InstanceOption[] | undefined;
  selected: Set<number>;
  onToggle: (id: number) => void;
  onToggleSession: (ids: number[], next: boolean) => void;
  /** Some instances the dialog was opened for are not correctable by this operator. */
  preselectedHidden?: boolean;
}) {
  const { sessions, loose } = useMemo(() => {
    const sessions = new Map<
      number,
      { label: string; items: InstanceOption[] }
    >();
    const loose: InstanceOption[] = [];
    for (const o of options ?? []) {
      if (o.sessionId == null) loose.push(o);
      else {
        const g = sessions.get(o.sessionId) ?? {
          label: o.sessionLabel ?? `Session ${o.sessionId}`,
          items: [],
        };
        g.items.push(o);
        sessions.set(o.sessionId, g);
      }
    }
    return { sessions: [...sessions.values()], loose };
  }, [options]);

  if (!options)
    return <div className="p-6 text-sm text-olive-11">Loading instances…</div>;
  if (options.length === 0)
    return (
      <div className="p-6 text-sm text-olive-11">
        There are no instances here you can correct.
      </div>
    );

  return (
    <div className="p-4 overflow-auto">
      <div className="max-w-xl mx-auto space-y-4">
        <p className="text-sm text-olive-11">
          Choose which instances this correction applies to. Unselected
          instances keep the current revision.
        </p>
        {preselectedHidden && (
          <p className="text-sm text-amber-700">
            Some of the instances you opened this for are not yours to correct,
            so they are not listed.
          </p>
        )}
        {loose.length > 0 && (
          <div className="border border-border">
            {loose.map((o) => (
              <label
                key={o.id}
                className="flex items-center gap-3 px-3 py-2 border-b border-border last:border-b-0 cursor-pointer hover:bg-muted/40"
              >
                <Checkbox
                  checked={selected.has(o.id)}
                  onCheckedChange={() => onToggle(o.id)}
                />
                <InstanceCaption o={o} />
                <span className="flex-1" />
                {o.status && (
                  <span className="text-xs text-olive-9">
                    {formatStatus(o.status)}
                  </span>
                )}
              </label>
            ))}
          </div>
        )}
        {sessions.map((g) => {
          const ids = g.items.map((i) => i.id);
          const allSelected = ids.every((id) => selected.has(id));
          return (
            <div key={g.label} className="border border-border">
              <label className="flex items-center gap-3 px-3 py-2 bg-olive-2 border-b border-border cursor-pointer">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={() => onToggleSession(ids, !allSelected)}
                />
                <span className="text-sm font-medium flex-1">{g.label}</span>
                <span className="text-xs text-olive-9">
                  {ids.length} instances · all-or-none
                </span>
              </label>
              {g.items.map((o) => (
                <div
                  key={o.id}
                  className="flex items-center gap-3 px-3 py-2 pl-9 border-b border-border last:border-b-0"
                >
                  <InstanceCaption o={o} />
                  <span className="flex-1" />
                  {o.status && (
                    <span className="text-xs text-olive-9">
                      {formatStatus(o.status)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Step 3: preview + commit.
export function ReviewStep({
  preview,
  instancesById,
  forwardPatch,
  setForwardPatch,
  canForwardPatch,
}: {
  preview: CorrectionPreviewData | undefined;
  instancesById: Map<number, InstanceOption>;
  forwardPatch: boolean;
  setForwardPatch: (v: boolean) => void;
  // The forward-patch (apply to the shared template draft) option is only
  // offered to operators who can edit templates — it has org-wide effect.
  canForwardPatch: boolean;
}) {
  if (!preview)
    return <div className="p-6 text-sm text-olive-11">Computing changes…</div>;

  const s = preview.summary;
  const blocked = preview.blockers.length > 0;

  return (
    <div className="p-4 space-y-4 overflow-auto">
      {blocked && (
        <div className="border border-red-600 bg-red-50 p-3 space-y-1">
          <div className="flex items-center gap-2 text-red-700 font-medium text-sm">
            <AlertTriangleIcon className="h-4 w-4" /> Cannot apply — resolve
            these first:
          </div>
          <ul className="text-sm text-red-700 list-disc pl-6">
            {preview.blockers.map((b, i) => (
              <li key={i}>{b.message}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-3 gap-px bg-border border border-border text-sm">
        <Stat
          label="Attributes"
          value={`+${s.attributesAdded} / −${s.attributesDeleted}`}
        />
        <Stat
          label="Columns"
          value={`+${s.columnsAdded} / −${s.columnsDeleted} / ✎${s.columnsRenamed}`}
        />
        <Stat label="Cells rewritten" value={String(s.cellsRewritten)} />
      </div>

      {s.valuesLost > 0 && (
        <div className="border border-red-600 bg-red-50 p-3 text-sm text-red-700 flex items-center gap-2">
          <AlertTriangleIcon className="h-4 w-4 shrink-0" />
          <span>
            <strong>{s.valuesLost}</strong> recorded value(s) across{" "}
            <strong>{s.instancesWithDataLoss}</strong> instance(s) will be
            permanently removed. Recoverable only by rolling back this
            correction.
          </span>
        </div>
      )}

      <div className="border border-border">
        {preview.deltas.map((d) => {
          const o = instancesById.get(d.instanceId);
          return (
            <div
              key={d.instanceId}
              className="border-b border-border last:border-b-0 px-3 py-2"
            >
              <div className="flex items-center justify-between">
                {o ? (
                  <InstanceCaption o={o} />
                ) : (
                  <span className="text-sm font-mono">#{d.instanceId}</span>
                )}
                {d.dataLoss && (
                  <span className="text-xs text-red-700">
                    {d.valuesLost} lost
                  </span>
                )}
              </div>
              {d.ops.length > 0 && (
                <ul className="mt-1 text-xs text-olive-11 space-y-0.5">
                  {d.ops.map((op, i) => (
                    <li key={i} className={op.dataLoss ? "text-red-700" : ""}>
                      {describeOp(op)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {canForwardPatch && (
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox
            checked={forwardPatch}
            onCheckedChange={(v) => setForwardPatch(!!v)}
          />
          Also apply these fixes to the template draft (for future instances)
        </label>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-background px-3 py-2">
      <div className="text-xs text-olive-9">{label}</div>
      <div className="font-mono">{value}</div>
    </div>
  );
}

function describeOp(
  op: CorrectionPreviewData["deltas"][number]["ops"][number],
): string {
  const target = op.columnName
    ? `${op.attributeName} → ${op.columnName}`
    : op.attributeName;
  switch (op.opType) {
    case "ADD_ATTRIBUTE":
      return `Add attribute "${target}"`;
    case "DELETE_ATTRIBUTE":
      return `Delete attribute "${target}"${op.valuesLost ? ` (${op.valuesLost} lost)` : ""}`;
    case "RENAME_ATTRIBUTE":
      return `Rename attribute "${target}"`;
    case "RECOMPUTE_ATTRIBUTE":
      return `Recompute "${target}"`;
    case "ADD_COLUMN":
      return `Add column "${target}"`;
    case "DELETE_COLUMN":
      return `Delete column "${target}"${op.valuesLost ? ` (${op.valuesLost} lost)` : ""}`;
    case "RENAME_COLUMN":
      return `Rename column "${target}" (${op.cellsRewritten} cells)`;
    case "RECOMPUTE_COLUMN":
      return `Recompute column "${target}"`;
    default:
      return op.opType;
  }
}

export function CorrectionFooter({
  step,
  onBack,
  onNext,
  onApply,
  canApply,
  applying,
  dataLoss,
}: {
  step: CorrectionStep;
  onBack: () => void;
  onNext: () => void;
  onApply: () => void;
  canApply: boolean;
  applying: boolean;
  dataLoss: boolean;
}) {
  return (
    <div className="flex items-center justify-between border-t border-border px-4 h-12 shrink-0">
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        disabled={step === "scope"}
      >
        Back
      </Button>
      {step !== "review" ? (
        <Button size="sm" onClick={onNext}>
          Next
        </Button>
      ) : (
        <Button
          size="sm"
          onClick={onApply}
          disabled={!canApply || applying}
          className={
            dataLoss ? "bg-red-600 text-white hover:bg-red-700" : undefined
          }
        >
          {applying
            ? "Applying…"
            : dataLoss
              ? "Apply (with data loss)"
              : "Apply correction"}
        </Button>
      )}
    </div>
  );
}
